import { useState, useEffect } from "react";
import LazyMultiSelect from "../InputComponents/Filters/LazyMultiSelect";
import { useMsal } from "@azure/msal-react";
import { AdminApi } from "../../Api/AdminApi";
import MultiSelect from "../InputComponents/Filters/MultiSelect";

export interface ProjectFilters {
  names: string[];        
  cordaStatuses: string[]; 
  codes: string[];
  statuses: string[];       
}

interface ProjectFilterProps {
  onFiltersChange: (filters: ProjectFilters) => void;
}

const ProjectFilter: React.FC<ProjectFilterProps> = ({ onFiltersChange }) => {
    const { instance, accounts } = useMsal();
    const api = new AdminApi(accounts[0], instance);
    const [filtersVisible, setFiltersVisible] = useState(true);
    const [selectedNames, setSelectedNames] = useState<string[]>([]);
    const [selectedCordaStatuses, setSelectedCordaStatuses] = useState<string[]>([]);
    const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

    // Liste des statuts disponibles pour questionnaireStatus
    const statusOptions = [
      { value: "0", label: "Opening" },
      { value: "1", label: "Consortium editing" },
      { value: "2", label: "Verification" },
      { value: "3", label: "Approval" },
      { value: "4", label: "End" }
    ];

    useEffect(() => {
      onFiltersChange({
        names: selectedNames,
        cordaStatuses: selectedCordaStatuses,
        codes: selectedCodes,
        statuses: selectedStatuses
      });
    }, [selectedNames, selectedCordaStatuses, selectedCodes, selectedStatuses, onFiltersChange]);
  
    // Handler to get selected values from the LazyMultiSelect component for names
    const handleNameSelectionChange = (values: string[]) => {
      console.log("Selected names:", values);
      setSelectedNames(values);
    };

    // Handler for Corda statuses
    const handleCordaStatusSelectionChange = (values: string[]) => {
      console.log("Selected Corda statuses:", values);
      setSelectedCordaStatuses(values);
    };

    // Handler for codes
    const handleCodeSelectionChange = (values: string[]) => {
      console.log("Selected codes:", values);
      setSelectedCodes(values);
    };

    // Handler for statuses
    const handleStatusSelectionChange = (values: string[]) => {
      console.log("Selected statuses:", values);
      setSelectedStatuses(values);
    };
  
    // Custom CSS style injected to ensure font consistency
    const injectCss = `
      .custom-form-control,
      .custom-form-select,
      .custom-form-select option {
        height: 60px !important;
        font-size: 19px !important;
        font-family: inherit !important;
      }
    `;
  
    // Style for the filter toggle button - more discreet
    const buttonStyle = {
      fontSize: "14px",
      fontWeight: 400,
      padding: "4px 8px",
      color: "#6c757d",
      textDecoration: "none",
      border: "none",
      background: "none"
    };
  
    return (
      <div className="mt-4 mb-4">
        {/* Custom CSS injection */}
        <style>{injectCss}</style>
        
        {/* Button to hide/show filters - more discreet and in English */}
        <div className="d-flex justify-content-end mb-2">
          <button 
            onClick={() => setFiltersVisible(!filtersVisible)}
            className="btn btn-sm text-secondary"
            style={buttonStyle}
          >
            {filtersVisible ? "Hide filters" : "Show filters"}
          </button>
        </div>

        {/* Filters - conditionally visible */}
        {filtersVisible && (
          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <LazyMultiSelect
                onSelectionChange={handleNameSelectionChange}
                placeholder="Filter by Project acronym"
                className="w-100" 
                fetchFunction={(query, page, limit) => api.getProjectNames(query, page, limit)}
                initialValue={selectedNames}
              />
            </div>
            <div className="col-md-3">
              <LazyMultiSelect
                onSelectionChange={handleCodeSelectionChange}
                placeholder="Filter by Project number"
                className="w-100" 
                fetchFunction={(query, page, limit) => api.getProjectCodes(query, page, limit)}
                initialValue={selectedCodes}
              />
            </div>
            <div className="col-md-3">
              <LazyMultiSelect
                onSelectionChange={handleCordaStatusSelectionChange}
                placeholder="Filter by CORDA status"
                className="w-100" 
                fetchFunction={(query, page, limit) => api.getProjectCordaStatus(query, page, limit)}
                initialValue={selectedCordaStatuses}
              />
            </div>
            <div className="col-md-3">
              <MultiSelect
                onSelectionChange={handleStatusSelectionChange}
                placeholder="Filter by Status"
                className="w-100"
                initialValue={selectedStatuses} 
                options={statusOptions}
              />
            </div>
          </div>
        )}
      </div>
    );
  };
  
  export default ProjectFilter;